import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
import { fetchGetSchemaSideFilter } from '../services/fetch-get-schema-side-filter';
import { TypeBlock } from '../types/filterElementsSchema';
import {
  connectionTemplateTab,
  fieldsAuto,
  FilterSchema,
  FilterTemplates,
  General,
  mapPriceCategory,
  PartConnectionTemplate,
  PriceConst,
} from '../types/filterSchema';

import { generateParams } from '@entities/Filter/module/helper/generate-params';
import { FilterParams, TabCarType } from '@entities/Filter';
import { arrayFilterSizeParams, mapTabProductCategoryTabType } from '@entities/Filter/module/const/const-params';
import { generateStrParams } from '@entities/Filter/module/helper/convertor-params';
import { ProductSpecifications } from '@entities/Product';
import config from '@app/config';

const initialState: FilterSchema = {
  template: null,
  strParams: '',
  shortStrParams: '',
  autoSelects: {
    mark: null,
    model: null,
    year: null,
    modification: null,
  },
  params: {},
  sparky: [],
  minPrice: null,
  maxPrice: null,
  protectorMinPrice: PriceConst.minPrice,
  protectorMaxPrice: PriceConst.maxPrice,
  schemaFilter: [],
  schemaParamsBlock: null,
  currentPage: 1,
  isInitial: false,
  isLoadingSchemaFilter: false,
  isSeoUrl: false,
  skipProductDataFetch: false,
};

export const filterSlice = createSlice({
  name: 'widgetFilter',
  initialState,
  reducers: {
    setSkipProductDataFetch: (state, action: PayloadAction<boolean>) => {
      state.skipProductDataFetch = action.payload;
    },
    setStrParams: state => {
      const { strParams, shortStrParams } = generateStrParams(state.params, state.template, state.currentPage);
      state.strParams = strParams;
      state.shortStrParams = shortStrParams;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setListParams: (state, action: PayloadAction<Array<{ key: string; value: any }>>) => {
      const listParams = action.payload;
      listParams.forEach(param => {
        state.params[param.key] = param.value;
      });
    },
    setParam: (state, action: PayloadAction<{ key: string; value: any }>) => {
      const param = action.payload;
      state.params[param.key] = param.value;
    },
    setMultiSelectParams: (state, action: PayloadAction<{ key: string; value: any }>) => {
      const param = action.payload;
      let array = Array.isArray(state.params[param.key]) ? [...state.params[param.key].map((x: number | string) => String(x))] : [];
      if (array.includes(param.value + '')) {
        array = array.filter(x => String(x) !== String(param.value));
      } else {
        array.push(param.value + '');
      }
      state.params[param.key] = array;
    },
    setBrands: (state, action: PayloadAction<{ key: string; value: any } | Array<{ key: string; value: any }>>) => {
      if (Array.isArray(action.payload)) {
        action.payload.forEach(brand => {
          const brandId = brand.value;
          let listBrand = Array.isArray(state.params[brand.key]) ? [...state.params[brand.key]] : [];
          if (listBrand.includes(brandId)) {
            listBrand = listBrand.filter(brand => brand !== brandId);
          } else {
            listBrand.push(brandId);
          }
          state.params[brand.key] = listBrand;
        });
      } else {
        const brandId = action.payload.value;
        let listBrand = Array.isArray(state.params[action.payload.key]) ? [...state.params[action.payload.key]] : [];
        if (listBrand.includes(brandId)) {
          listBrand = listBrand.filter(brand => brand !== brandId);
        } else {
          listBrand.push(brandId);
        }
        state.params[action.payload.key] = listBrand;
      }
    },
    setTemplate: (state, action: PayloadAction<FilterTemplates>) => {
      state.template = action.payload;
    },
    setAuto: (state, action: PayloadAction<{ key: fieldsAuto; value: any }>) => {
      state.sparky = [];
      const field = action.payload.key;
      if (field === fieldsAuto.Mark) {
        state.autoSelects[fieldsAuto.Mark] = action.payload.value;
        state.params[fieldsAuto.Mark] = action.payload.value?.code;
        state.autoSelects[fieldsAuto.Model] = null;
        state.autoSelects[fieldsAuto.Year] = null;
        state.autoSelects[fieldsAuto.CarBody] = null;
        state.autoSelects[fieldsAuto.Modification] = null;
      }
      if (field === fieldsAuto.Model) {
        state.autoSelects[fieldsAuto.Model] = action.payload.value;
        state.params[fieldsAuto.Model] = action.payload.value?.groupCode;
        state.autoSelects[fieldsAuto.Year] = null;
        state.autoSelects[fieldsAuto.CarBody] = null;
        state.autoSelects[fieldsAuto.Modification] = null;
      }
      if (field === fieldsAuto.Year) {
        state.autoSelects[fieldsAuto.Year] = action.payload.value;
        state.params[fieldsAuto.Year] = action.payload.value;
        state.autoSelects[fieldsAuto.CarBody] = null;
        state.autoSelects[fieldsAuto.Modification] = null;
      }
      if (field === fieldsAuto.CarBody) {
        state.autoSelects[fieldsAuto.CarBody] = action.payload.value;
      }
      if (field === fieldsAuto.Modification) {
        state.autoSelects[fieldsAuto.Modification] = action.payload.value;
        state.params[fieldsAuto.Modification] = action.payload.value?.code;
      }
    },
    resetAuto: state => {
      state.autoSelects[fieldsAuto.Mark] = null;
      state.autoSelects[fieldsAuto.Model] = null;
      state.autoSelects[fieldsAuto.Year] = null;
      state.autoSelects[fieldsAuto.CarBody] = null;
      state.autoSelects[fieldsAuto.Modification] = null;
      state.params[fieldsAuto.Mark] = null;
      state.params[fieldsAuto.Model] = null;
      state.params[fieldsAuto.Year] = null;
      state.params[fieldsAuto.CarBody] = null;
      state.params[fieldsAuto.Modification] = null;
      state.sparky = [];
    },
    setProductCategoryTab: (state, action: PayloadAction<TabCarType>) => {
      const productCategoryTab = mapTabProductCategoryTabType[action.payload];
      const template = connectionTemplateTab[action.payload];

      [...arrayFilterSizeParams, General.StrictMode].forEach(param => {
        state.params[param] = null;
      });
      state.params[FilterParams.ParamTab] = productCategoryTab;
      state.template = template;
      state.sparky = [];
      const { strParams, shortStrParams } = generateStrParams(state.params, state.template, state.currentPage);
      state.strParams = strParams;
      state.shortStrParams = shortStrParams;
    },
    setMinPrice: (state, action: PayloadAction<number>) => {
      const price = state.protectorMinPrice > action.payload ? state.protectorMinPrice : action.payload;
      state.minPrice = price;
      const key = mapPriceCategory[state.template]?.minPrice;
      if (key) {
        state.params[key] = action.payload === state.protectorMinPrice ? null : price;
      }
    },
    setMaxPrice: (state, action: PayloadAction<number>) => {
      const price = state.protectorMaxPrice < action.payload ? state.protectorMaxPrice : action.payload;
      state.maxPrice = price;
      const key = mapPriceCategory[state.template]?.maxPrice;
      if (key) {
        state.params[key] = action.payload === state.protectorMaxPrice ? null : price;
      }
    },
    setSparky: (state, action: PayloadAction<number[]>) => {
      state.sparky = action.payload;
    },
    setIsSeoUrl: (state, action: PayloadAction<boolean>) => {
      state.isSeoUrl = action.payload;
    },
    reset: state => {
      const strParams = `?part=${PartConnectionTemplate[state.template]}`;
      return {
        ...initialState,
        minPrice: state.protectorMinPrice,
        maxPrice: state.protectorMaxPrice,
        protectorMinPrice: state.protectorMinPrice,
        protectorMaxPrice: state.protectorMaxPrice,
        template: state.template,
        schemaFilter: state.schemaFilter,
        schemaParamsBlock: state.schemaParamsBlock,
        strParams,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(HYDRATE, (state, action: any) => {
      return {
        ...state,
        ...action.payload.widgetFilter,
      };
    });
    builder.addCase(fetchGetSchemaSideFilter.pending, state => {
      state.isLoadingSchemaFilter = true;
    });
    builder.addCase(fetchGetSchemaSideFilter.fulfilled, (state, action) => {
      if (action.payload) {
        const schema = [...action.payload];
        if (config.safeModeFilterOnlyMainParam) {
          schema.forEach(block => {
            if (
              ![TypeBlock.DisksSelects, TypeBlock.OilsSelects, TypeBlock.TruckTyresSelects, TypeBlock.TyresSelects].includes(
                block.type as TypeBlock,
              ) &&
              !block?.elements?.some(element =>
                [
                  ProductSpecifications.AkbPolarity,
                  ProductSpecifications.AkbDimensions,
                  ProductSpecifications.AkbCranking,
                  ProductSpecifications.AkbCapacity,
                  ProductSpecifications.OilViscosity,
                  ProductSpecifications.OilVolume,
                  ProductSpecifications.OilType,
                  ProductSpecifications.OilAcea,
                  ProductSpecifications.OilApi,
                ].includes(element.name as ProductSpecifications),
              )
            ) {
              block.disabled = true;
            }
          });
        }

        state.schemaFilter = schema;

        // дефолтные значения для блока цены
        const priceBlock = action.payload.find(block => block.type === TypeBlock.PriceBlock);
        if (priceBlock) {
          state.minPrice = priceBlock.elements[0]?.settings?.minPrice;
          state.maxPrice = priceBlock.elements[0]?.settings?.maxPrice;
          state.protectorMinPrice = priceBlock.elements[0]?.settings?.minPrice;
          state.protectorMaxPrice = priceBlock.elements[0]?.settings?.maxPrice;
        }

        if (!state.isInitial) {
          // блок для отрисовки таба в мобильной версии
          state.schemaParamsBlock =
            action.payload.find(block =>
              [TypeBlock.DisksSelects, TypeBlock.OilsSelects, TypeBlock.TruckTyresSelects, TypeBlock.TyresSelects].includes(
                block.type as TypeBlock,
              ),
            ) || null;

          state.params = generateParams(action.payload);
          state.isInitial = true;
          const { strParams } = generateStrParams(state.params, state.template, state.currentPage);
          state.strParams = strParams;
        }
      }
      state.isLoadingSchemaFilter = false;
    });
    builder.addCase(fetchGetSchemaSideFilter.rejected, state => {
      state.isLoadingSchemaFilter = true;
    });
  },
});

export const { actions: filterActions } = filterSlice;
